import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { pageApi } from "../services/page.service";
import { integrationsApi } from "../services/integrations.service";
import { useIntegrationsStore } from "../stores/integrations.store";
import { useWorkspaceStore } from "../stores/workspace.store";
import { IconRenderer } from "../lib/icon-map";
import ComponentPreview from "../components/builder/ComponentPreview";
import ComponentEditModal from "../components/builder/ComponentEditModal";
import PageGrid from "../components/PageGrid";
import DataEditModal from "../components/builder/DataEditModal";
import ColumnVisibilityModal from "../components/builder/ColumnVisibilityModal";
import ChartFieldsModal from "../components/builder/ChartFieldsModal";
import FormModal from "../components/builder/FormModal";
import { PageComponent } from "../types/components";
import { findComponentDeep, updateComponentDeep } from "../lib/component-tree";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useContainerWidth } from "../hooks/use-container-width";
import { useWorkspaceRole } from "../hooks/use-workspace-role";
import { useAuthStore } from "../stores/auth.store";
import { useThemePreset } from "../hooks/use-theme-preset";
import { PianoTopbar } from "../components/PianoTopbar";
import { DashboardTopbar } from "../components/DashboardTopbar";
import { CtrlTopbar } from "../components/CtrlTopbar";

export default function PageView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspaceStore();
  const { canManageStructure, canEditData } = useWorkspaceRole();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const queryClient = useQueryClient();
  const { containerRef, width, height } = useContainerWidth();
  const themePreset = useThemePreset();
  const isPiano = themePreset === "piano";
  const isDashboard = themePreset === "dashboard";
  const isCtrl = themePreset === "ctrl";
  const [editingComponent, setEditingComponent] =
    useState<PageComponent | null>(null);
  const [editingDataComponent, setEditingDataComponent] =
    useState<PageComponent | null>(null);
  const [editingColumnsComponent, setEditingColumnsComponent] =
    useState<PageComponent | null>(null);
  const [editingChartFieldsComponent, setEditingChartFieldsComponent] =
    useState<PageComponent | null>(null);
  const [activeFormComponentId, setActiveFormComponentId] = useState<
    string | null
  >(null);
  const [allWorkspaceComponents, setAllWorkspaceComponents] = useState<
    PageComponent[]
  >([]);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    // Evita vazamento de estado visual entre páginas ao trocar de slug.
    setEditingComponent(null);
    setEditingDataComponent(null);
    setEditingColumnsComponent(null);
    setEditingChartFieldsComponent(null);
    setActiveFormComponentId(null);
    setActiveFilters({});
  }, [slug]);

  const { data: page, isLoading } = useQuery({
    queryKey: ["page", currentWorkspace?.id, slug],
    queryFn: () => pageApi.getOne(currentWorkspace!.id, slug!),
    enabled: !!currentWorkspace && !!slug,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: "always",
  });

  const isSameRoutePage = !!page && !!slug && page.slug === slug;

  // Carregar todos os componentes do workspace
  useEffect(() => {
    const loadAllWorkspaceComponents = async () => {
      if (!currentWorkspace?.id) return;

      try {
        const allComps = await pageApi.getAllComponents(currentWorkspace.id);
        setAllWorkspaceComponents(allComps);
      } catch (error) {
        console.error("Failed to load workspace components:", error);
      }
    };

    loadAllWorkspaceComponents();
  }, [currentWorkspace, page]); // Recarregar quando a página mudar também

  // Carrega as integrações do workspace no store global assim que a página abre.
  // Sem isso, getIntegrationById() só encontrava integrações depois de alguém
  // abrir a config de algum componente (única tela que buscava integrações antes
  // disso) — então tabelas via Google Sheets ficavam sem sincronizar até isso
  // acontecer, especialmente em uma sessão/recarregamento novo.
  const { setIntegrations: setIntegrationsStore } = useIntegrationsStore();
  const { data: workspaceIntegrations } = useQuery({
    queryKey: ["integrations", currentWorkspace?.id],
    queryFn: () => integrationsApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  useEffect(() => {
    if (workspaceIntegrations) setIntegrationsStore(workspaceIntegrations);
  }, [workspaceIntegrations]);

  const updatePageMutation = useMutation({
    mutationFn: (components: PageComponent[]) =>
      pageApi.update(currentWorkspace!.id, slug!, { components }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["page", currentWorkspace?.id, slug],
      });
    },
  });

  const updateDataMutation = useMutation({
    mutationFn: (components: PageComponent[]) =>
      pageApi.updateData(currentWorkspace!.id, slug!, { components }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["page", currentWorkspace?.id, slug],
      });
    },
  });

  const handleEditComponent = (component: PageComponent) => {
    setEditingComponent(component);
  };

  const handleSaveComponent = (updatedComponent: PageComponent) => {
    if (!page?.components) return;

    const updatedComponents = updateComponentDeep(
      page.components,
      updatedComponent.id,
      () => updatedComponent,
    );

    updatePageMutation.mutate(updatedComponents);
    setEditingComponent(null);
  };

  const handleDataSave = (newData: any[]) => {
    if (!page?.components || !editingDataComponent) return;

    const updatedComponents = updateComponentDeep(
      page.components,
      editingDataComponent.id,
      (c: PageComponent) => {
        // Para kanban, salvamos os dados em columns
        if (c.type === "kanban") {
          return { ...c, config: { ...c.config, columns: newData } };
        }

        // Para outros componentes, salvamos em dataSource.data
        return {
          ...c,
          config: {
            ...c.config,
            dataSource: {
              type: "static",
              ...c.config.dataSource,
              data: newData,
            },
          },
        };
      },
    );

    updateDataMutation.mutate(updatedComponents);
    setEditingDataComponent(null);
  };

  const handleColumnsSave = (columns: any[]) => {
    if (!page?.components || !editingColumnsComponent) return;

    const updatedComponents = updateComponentDeep(
      page.components,
      editingColumnsComponent.id,
      (c: PageComponent) => ({ ...c, config: { ...c.config, columns } }),
    );

    updatePageMutation.mutate(updatedComponents);
    setEditingColumnsComponent(null);
  };

  const handleChartFieldsSave = (
    xKey: string,
    yKey: string,
    groupDuplicates: boolean,
    limit?: number,
    sortOrder?: "asc" | "desc"
  ) => {
    if (!page?.components || !editingChartFieldsComponent) return;

    const updatedComponents = updateComponentDeep(
      page.components,
      editingChartFieldsComponent.id,
      (c: PageComponent) => ({
        ...c,
        config: {
          ...c.config,
          xKey,
          yKey,
          groupDuplicates,
          limit,
          sortOrder,
        },
      }),
    );

    updatePageMutation.mutate(updatedComponents);
    setEditingChartFieldsComponent(null);
  };

  // Resolve uma amostra de componente fonte para o ChartFieldsModal listar campos
  // disponíveis quando o gráfico usa fonte dinâmica (via filtro "Tabela Vinculada").
  const resolveChartSampleSourceId = (config: any): string | undefined => {
    if (!config?.dynamicSourceField) return config?.sourceComponentId;
    const filterComp = (page?.components || []).find(
      (c: any) => c.type === "filter" && c.config?.field === config.dynamicSourceField,
    );
    return filterComp?.config?.options?.[0]?.value;
  };

  const handleButtonClick = (componentId: string) => {
    setActiveFormComponentId(componentId);
  };

  const handleComponentChange = (componentId: string, newConfig: any) => {
    if (!page?.components) return;

    // Busca recursiva para suportar componentes dentro de containers.
    const updatedComponents = updateComponentDeep(
      page.components,
      componentId,
      (c) => ({ ...c, config: newConfig }),
    );

    updateDataMutation.mutate(updatedComponents);
  };

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFormSubmit = (data: any) => {
    if (!activeFormComponentId || !page?.components) return;

    const buttonComponent = page.components.find(
      (c: PageComponent) => c.id === activeFormComponentId
    );
    if (!buttonComponent?.config.sourceComponentId) return;

    // Buscar primeiro nos componentes locais
    let sourceComponent = page.components.find(
      (c: PageComponent) => c.id === buttonComponent.config.sourceComponentId
    );

    // Se não encontrou localmente, buscar nos componentes de outras páginas
    if (!sourceComponent) {
      sourceComponent = allWorkspaceComponents.find(
        (c) => c.id === buttonComponent.config.sourceComponentId
      );
    }

    if (!sourceComponent) return;

    const currentData = sourceComponent.config.dataSource?.data || [];

    // Se for lista, adicionar ID automático
    const newData =
      sourceComponent.type === "list"
        ? {
            ...data,
            id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          }
        : data;

    const updatedData = [...currentData, newData];

    const updatedComponents = page.components.map((c: PageComponent) =>
      c.id === sourceComponent.id
        ? {
            ...c,
            config: {
              ...c.config,
              dataSource: {
                ...c.config.dataSource,
                type: "static",
                data: updatedData,
              },
            },
          }
        : c
    );

    updateDataMutation.mutate(updatedComponents);
    setActiveFormComponentId(null);
  };

  const getFormColumns = () => {
    if (!activeFormComponentId || !page?.components) return [];

    const buttonComponent = page.components.find(
      (c: PageComponent) => c.id === activeFormComponentId
    );
    if (!buttonComponent?.config.sourceComponentId) return [];

    // Buscar primeiro nos componentes locais
    let sourceComponent = page.components.find(
      (c: PageComponent) => c.id === buttonComponent.config.sourceComponentId
    );

    // Se não encontrou localmente, buscar nos componentes de outras páginas
    if (!sourceComponent) {
      sourceComponent = allWorkspaceComponents.find(
        (c) => c.id === buttonComponent.config.sourceComponentId
      );
    }

    if (!sourceComponent) return [];

    if (sourceComponent.type === "table" && sourceComponent.config.columns) {
      return sourceComponent.config.columns.map((col: any) => ({
        field: col.field,
        label: col.label,
        type: col.type,
      }));
    }

    // Se for lista, usar campos padrão do ListWidget
    if (sourceComponent.type === "list") {
      return [
        { field: "title", label: "Título", type: "text" },
        { field: "subtitle", label: "Subtítulo", type: "text" },
        { field: "icon", label: "Ícone (emoji)", type: "text" },
        { field: "badge", label: "Badge", type: "text" },
      ];
    }

    return [];
  };

  if (isLoading || !slug || (!!page && !isSameRoutePage)) {
    return <div>Carregando página...</div>;
  }

  if (!page) {
    return <div>Página não encontrada</div>;
  }

  // isLocked bloqueia edição para todos, exceto super_admin
  const isLocked = !!((page as any)?.permissions?.isLocked) && !isSuperAdmin;
  const effectiveCanManageStructure = canManageStructure && !isLocked;
  const effectiveCanEditData = canEditData && !isLocked;

  // Separar componentes de filtro dos outros
  const filterComponents = page.components?.filter((c: PageComponent) => c.type === 'filter') || [];
  const nonFilterComponents = page.components?.filter((c: PageComponent) => c.type !== 'filter') || [];

  return (
    <div className="h-full flex flex-col">
      {isPiano ? (
        <PianoTopbar pageName={page.name} pageCategory={page.description ? page.description.split(" ")[0].toUpperCase() : "VISÃO GERAL"} />
      ) : isDashboard ? (
        <DashboardTopbar pageName={page.name} />
      ) : isCtrl ? (
        <CtrlTopbar pageName={page.name} />
      ) : (
      <div className="flex items-center justify-start gap-8 mb-6 flex-shrink-0 border-b border-border/50 pb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 rounded-lg">
            <IconRenderer icon={page.icon} className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{page.name}</h1>
            {page.description && (
              <p className="text-muted-foreground mt-1">{page.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 ml-auto mr-6">
          {/* Filtros na mesma linha do título */}
          {filterComponents.length > 0 && (
            <div className="flex items-center gap-3 px-3 py-1.5 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center gap-4">
                {filterComponents.map((component: PageComponent, index: number) => (
                  <div key={component.id} className="flex items-center gap-4">
                    <ComponentPreview
                      component={component}
                      onEdit={effectiveCanManageStructure ? handleEditComponent : undefined}
                      onDataEditClick={effectiveCanEditData ? (id) => {
                        const comp = findComponentDeep(page.components, id);
                        if (comp) setEditingDataComponent(comp);
                      } : undefined}
                      onColumnsEditClick={effectiveCanManageStructure ? (id) => {
                        const comp = findComponentDeep(page.components, id);
                        if (comp) setEditingColumnsComponent(comp);
                      } : undefined}
                      onChartFieldsEditClick={effectiveCanManageStructure ? (id) => {
                        const comp = findComponentDeep(page.components, id);
                        if (comp) setEditingChartFieldsComponent(comp);
                      } : undefined}
                      onButtonClick={effectiveCanEditData ? handleButtonClick : undefined}
                      onComponentChange={effectiveCanEditData ? handleComponentChange : undefined}
                      activeFilters={activeFilters}
                      onFilterChange={handleFilterChange}
                      allComponents={
                        allWorkspaceComponents.length > 0
                          ? allWorkspaceComponents
                          : page.components
                      }
                      isCompact={true}
                    />
                    {index < filterComponents.length - 1 && (
                      <div className="w-px h-6 bg-border"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Edit button removed */}
        </div>
      </div>
      )}

      <div ref={containerRef} className="w-full flex-1" style={{ maxWidth: 'none', minHeight: '600px' }}>
        <PageGrid
          gridKey={page.id}
          components={nonFilterComponents}
          width={width}
          height={height}
          isDraggable={false}
          isResizable={false}
        emptyState={
          <Card className="text-center py-12">
            <p className="text-muted-foreground">
              Esta página ainda não tem componentes.
            </p>
            {effectiveCanManageStructure && (
              <Button
                variant="link"
                onClick={() => navigate(`/pages/${slug}/edit`)}
                className="mt-4"
              >
                Adicionar componentes
              </Button>
            )}
          </Card>
        }
      >
        {(component) => (
          <ComponentPreview
            component={component}
            onEdit={effectiveCanManageStructure ? handleEditComponent : undefined}
            onDataEditClick={effectiveCanEditData ? (id) => {
              const comp = findComponentDeep(page.components, id);
              if (comp) setEditingDataComponent(comp);
            } : undefined}
            onColumnsEditClick={effectiveCanManageStructure ? (id) => {
              const comp = findComponentDeep(page.components, id);
              if (comp) setEditingColumnsComponent(comp);
            } : undefined}
            onChartFieldsEditClick={effectiveCanManageStructure ? (id) => {
              const comp = findComponentDeep(page.components, id);
              if (comp) setEditingChartFieldsComponent(comp);
            } : undefined}
            onButtonClick={effectiveCanEditData ? handleButtonClick : undefined}
            onComponentChange={effectiveCanEditData ? handleComponentChange : undefined}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            allComponents={
              allWorkspaceComponents.length > 0
                ? allWorkspaceComponents
                : page.components
            }
          />
        )}
        </PageGrid>
      </div>

      {editingComponent && (
        <ComponentEditModal
          component={editingComponent}
          onSave={handleSaveComponent}
          onClose={() => setEditingComponent(null)}
          availableComponents={
            allWorkspaceComponents.length > 0
              ? allWorkspaceComponents
              : page?.components || []
          }
          pageComponents={page?.components || []}
        />
      )}

      {editingDataComponent && (() => {
        // Para relationship-table, atualizar os dados das tabelas de origem
        let updatedConfig = editingDataComponent.config;
        if (editingDataComponent.type === 'relationship-table' && page?.components) {
          // Buscar dados atualizados das tabelas de origem
          if (editingDataComponent.config.leftTable?.id) {
            const leftTableComponent = page.components.find((c: PageComponent) => c.id === editingDataComponent.config.leftTable.id);
            if (leftTableComponent) {
              updatedConfig = {
                ...updatedConfig,
                leftTable: {
                  ...updatedConfig.leftTable,
                  data: leftTableComponent.config?.dataSource?.data || [],
                },
              };
            }
          }
          
          if (editingDataComponent.config.rightTable?.id) {
            const rightTableComponent = page.components.find((c: PageComponent) => c.id === editingDataComponent.config.rightTable.id);
            if (rightTableComponent) {
              updatedConfig = {
                ...updatedConfig,
                rightTable: {
                  ...updatedConfig.rightTable,
                  data: rightTableComponent.config?.dataSource?.data || [],
                },
              };
            }
          }
        }
        
        return (
          <DataEditModal
            isOpen={true}
            onClose={() => setEditingDataComponent(null)}
            onSave={handleDataSave}
            data={
              editingDataComponent.type === "kanban"
                ? editingDataComponent.config.columns || []
                : editingDataComponent.config.dataSource?.data || []
            }
            componentType={editingDataComponent.type as any}
            columns={editingDataComponent.config.columns}
            config={updatedConfig}
            allComponents={page?.components || []}
            title={`Editar Dados - ${
              editingDataComponent.config.title || editingDataComponent.type
            }`}
          />
        );
      })()}

      {editingColumnsComponent && (
        <ColumnVisibilityModal
          columns={editingColumnsComponent.config.columns || []}
          onClose={() => setEditingColumnsComponent(null)}
          onSave={handleColumnsSave}
        />
      )}


      {editingChartFieldsComponent && (
        <ChartFieldsModal
          chartType={editingChartFieldsComponent.config.chartType || "line"}
          data={editingChartFieldsComponent.config.dataSource?.data || []}
          currentXKey={editingChartFieldsComponent.config.xKey}
          currentYKey={editingChartFieldsComponent.config.yKey}
          sourceComponentId={resolveChartSampleSourceId(editingChartFieldsComponent.config)}
          allComponents={
            allWorkspaceComponents.length > 0
              ? allWorkspaceComponents
              : page?.components || []
          }
          groupDuplicates={editingChartFieldsComponent.config.groupDuplicates}
          limit={editingChartFieldsComponent.config.limit}
          sortOrder={editingChartFieldsComponent.config.sortOrder}
          onClose={() => setEditingChartFieldsComponent(null)}
          onSave={handleChartFieldsSave}
        />
      )}

      {activeFormComponentId && (
        <FormModal
          columns={getFormColumns()}
          onClose={() => setActiveFormComponentId(null)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}
